import React from 'react';
import { useQuery, useQueryClient } from 'react-query';

import { PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { deleteCommunityContact, getCommunityContacts } from 'apis/CommunityAPI';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import {
  SimpleListModal,
  SimpleListModalContent,
  SimpleListModalDescription,
  SimpleListModalHeaderActions,
  SimpleListModalTitleStrong,
} from 'components/lib/SimpleListModal';
import { SimpleListModalStickyFooter } from 'components/lib/SimpleListModalComponents';
import { getErrorMessageByPriority } from 'helpers/emails';
import { CommunityContactType, CommunityType } from 'types/communities';

import ManageContactsEntryModal from './ManageContactsEntryModal';
import ManageContactsTable from './ManageContactsTable';

type Props = {
  openModal: boolean;
  communityInfo: CommunityType | undefined;
  communityContacts?: CommunityContactType[];
  isFetchingCommunityContacts?: boolean;
  isLoadingCommunityContacts?: boolean;
  onClose: () => void;
};

type EntryModalState = {
  open: boolean;
  item?: CommunityContactType;
};

const StyledManageContactsModal = styled(SimpleListModal)`
  &.ant-modal {
    width: 1200px !important;
    max-width: min(95vw, 1200px) !important;
  }

  .ant-modal-content {
    width: 100% !important;
    max-width: 100% !important;
  }

  .ant-modal-body {
    height: 560px;
  }
`;

const ManageContactsModal = ({
  openModal,
  communityInfo,
  communityContacts: providedCommunityContacts,
  isFetchingCommunityContacts: providedIsFetchingCommunityContacts,
  isLoadingCommunityContacts: providedIsLoadingCommunityContacts,
  onClose,
}: Props) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const [entryModal, setEntryModal] = React.useState<EntryModalState>({ open: false });
  const [deletingContactId, setDeletingContactId] = React.useState<number | null>(null);
  const [contactToDelete, setContactToDelete] = React.useState<CommunityContactType | null>(null);
  const communityId = communityInfo?.id;
  const hasProvidedCommunityContacts = providedCommunityContacts !== undefined;
  const {
    data: fetchedCommunityContacts = [],
    isFetching: isFetchingFetchedCommunityContacts,
    isLoading: isLoadingFetchedCommunityContacts,
  } = useQuery<CommunityContactType[]>({
    queryKey: ['community', 'contacts', communityId],
    queryFn: () => getCommunityContacts(communityId as number),
    enabled: Boolean(openModal && communityId && !hasProvidedCommunityContacts),
    refetchOnWindowFocus: false,
    onError: () => {
      showMessage('error', 'Failed to fetch community contacts.');
    },
  });

  const communityContacts = providedCommunityContacts ?? fetchedCommunityContacts;
  const isFetchingCommunityContacts = providedIsFetchingCommunityContacts ?? isFetchingFetchedCommunityContacts;
  const isLoadingCommunityContacts = providedIsLoadingCommunityContacts ?? isLoadingFetchedCommunityContacts;

  const invalidateCommunityContacts = React.useCallback(() => {
    void queryClient.invalidateQueries(['community', 'contacts', communityId]);
    void queryClient.invalidateQueries(['shop-request', 'community', 'activities', communityId]);
  }, [communityId, queryClient]);

  const handleDelete = async () => {
    if (!contactToDelete?.id || deletingContactId) {
      return;
    }

    setDeletingContactId(contactToDelete.id);

    try {
      await deleteCommunityContact(communityId as number, contactToDelete.id);
      invalidateCommunityContacts();
      showMessage('success', 'Community contact deleted successfully.');
      setContactToDelete(null);
    } catch (error: any) {
      showMessage(
        'error',
        getErrorMessageByPriority(error?.response?.data, 'Failed to delete community contact.', [
          'email',
          'name',
          'shop_request_contact_enabled',
          'non_field_errors',
          'detail',
        ])
      );
    } finally {
      setDeletingContactId(null);
    }
  };

  const openCreateModal = () => {
    setEntryModal({ open: true });
  };

  const openEditModal = (item: CommunityContactType) => {
    setEntryModal({ open: true, item });
  };

  return (
    <>
      <ConfirmModal
        open={Boolean(contactToDelete)}
        title="Delete Contact?"
        description={`Are you sure you want to delete ${contactToDelete?.email ?? 'this contact'}?`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setContactToDelete(null)}
        loading={Boolean(deletingContactId)}
      />
      <StyledManageContactsModal
        open={openModal}
        onCancel={onClose}
        footer={null}
        title="Manage Community Contacts"
        centered
        destroyOnClose
      >
        <SimpleListModalContent>
          <SimpleListModalDescription>
            <div>
              Manage the registered contacts for{' '}
              <SimpleListModalTitleStrong>{communityInfo?.comunity_name}</SimpleListModalTitleStrong>.
            </div>
            <div>Add, edit, or remove contacts used for community matching.</div>
          </SimpleListModalDescription>

          <SimpleListModalHeaderActions>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Add Contact
            </Button>
          </SimpleListModalHeaderActions>

          <ManageContactsTable
            communityContacts={communityContacts}
            deletingContactId={deletingContactId}
            isFetchingCommunityContacts={isFetchingCommunityContacts}
            isLoadingCommunityContacts={isLoadingCommunityContacts}
            onDelete={setContactToDelete}
            onEdit={openEditModal}
          />

          <SimpleListModalStickyFooter>
            <Button onClick={onClose}>Close</Button>
          </SimpleListModalStickyFooter>
        </SimpleListModalContent>
      </StyledManageContactsModal>

      <ManageContactsEntryModal
        open={entryModal.open}
        item={entryModal.item}
        communityId={communityId}
        communityContacts={communityContacts}
        onClose={() => setEntryModal({ open: false })}
        onSaved={invalidateCommunityContacts}
      />
    </>
  );
};

export default ManageContactsModal;
